import React, { useState, useEffect } from 'react';
import { manageEnvelopeDocRules } from '../../../../../services/CommonService';
import { toast } from 'react-toastify';
import Select from 'react-select';
import './DocumentValidation.css';

const DocumentValidation = ({ envelopeId, envelopeData, isReadOnly = false }) => {

  const [minSize, setMinSize] = useState('');
  const [maxSize, setMaxSize] = useState('');
  const [resolution, setResolution] = useState('');
  const [maxPages, setMaxPages] = useState('');
  const [selectedFormats, setSelectedFormats] = useState([]); // Start with no formats selected
  const [isSuccess, setIsSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [existingDataLoaded, setExistingDataLoaded] = useState(false);
  const [hasExplicitlyLoadedFormats, setHasExplicitlyLoadedFormats] = useState(false);

  // Error states for validation messages
  const [minSizeError, setMinSizeError] = useState('');
  const [maxSizeError, setMaxSizeError] = useState('');
  const [resolutionError, setResolutionError] = useState('');
  const [maxPagesError, setMaxPagesError] = useState('');
  // const [formatsError, setFormatsError] = useState(''); // Removed format validation error


  // Content Requirements state
  const [contentRequirements, setContentRequirements] = useState({
    ocrReadable: false,
    noPasswordProtected: false,
    barcodeQrCode: false,
    signatureStamp: false
  });

  // Metadata Requirements state
  const [metadataRequired, setMetadataRequired] = useState(false);

  // File Naming Conventions state
  // const [namingPattern, setNamingPattern] = useState('<Document Name><Today\'s Date><Today\'s Date><Today\'s Date><Document Name>');
  // const [currentPatterns, setCurrentPatterns] = useState(['<Today\'s Date>_<Document Name>_<Last Name>_<First Name>']);

  // Process/Compliance state
  const [expiryValidation, setExpiryValidation] = useState(false);

  const formats = [
    { value: "pdf", label: "PDF" },
    { value: "jpg", label: "JPG" },
    { value: "jpeg", label: "JPEG" },
    { value: "png", label: "PNG" },
    { value: "tiff", label: "TIFF" },
    { value: "heic", label: "HEIC" },
    { value: "doc", label: "DOC" },
    { value: "docx", label: "DOCX" },
    { value: "odt", label: "ODT" },
    { value: "xlsx", label: "XLSX" },
    { value: "csv", label: "CSV" },
    { value: "ods", label: "ODS" },
    { value: "ppt", label: "PPT" },
    { value: "pptx", label: "PPTX" },
    { value: "odp", label: "ODP" },
    { value: "txt", label: "TXT" },
    { value: "rtf", label: "RTF" }
  ];



  // Validate envelopeId prop and load existing validation rules
  useEffect(() => {
    if (!envelopeId) {
      // No envelopeId provided
    } else {
      // Load existing validation rules if available
      if (envelopeData && !isReadOnly) {
        // Always load validation rules if envelopeData exists
        loadExistingValidationRules(envelopeData);
      }
    }
  }, [envelopeId, envelopeData, isReadOnly]);

  // Load existing data immediately when in read-only mode
  useEffect(() => {
    if (isReadOnly && envelopeData) {
      // Always load validation rules if envelopeData exists
      loadExistingValidationRules(envelopeData);
    }
  }, [isReadOnly, envelopeData]);

  // Remove the useEffect that was resetting selectedFormats to empty array
  // This was interfering with loading existing data from envelopeData





  const formatEnvelopeId = (id) => {
    if (!id) return null;
    // If id is already a number, validate it
    if (typeof id === 'number') {
      return id > 0 ? id : null;
    }
    // If id is a string, try to parse it
    const numId = parseInt(id);
    return isNaN(numId) || numId <= 0 ? null : numId;
  };

  // Function to load existing validation rules
  const loadExistingValidationRules = (rules) => {
    if (!rules) return;

    setLoading(true);

    try {
      // Load file formats - handle both cases: when formats exist and when they don't
      if (rules.allowed_formats && Array.isArray(rules.allowed_formats)) {
        // Filter out any invalid formats
        const validFormats = rules.allowed_formats.filter(format =>
          format && typeof format === 'string' && format.trim().length > 0
        );

        if (validFormats.length > 0) {
          const loadedFormats = validFormats.map(format => format.toLowerCase());
          const formatOptions = formats.filter(format => loadedFormats.includes(format.value));
          setSelectedFormats(formatOptions);
          setHasExplicitlyLoadedFormats(true);
        } else {
          // Empty array of formats - user didn't select any
          setSelectedFormats([]);
          setHasExplicitlyLoadedFormats(true); // Mark as explicitly loaded (even if empty)
        }
      } else {
        // No formats field in data - user didn't select any
        setSelectedFormats([]);
        setHasExplicitlyLoadedFormats(true); // Mark as explicitly loaded
      }

      // Load file size settings
      if (rules.min_size_kb) {
        setMinSize(rules.min_size_kb.toString());
      }
      if (rules.max_size_kb) {
        setMaxSize(rules.max_size_kb.toString());
      }

      // Load quality settings
      if (rules.min_dpi) {
        setResolution(rules.min_dpi.toString());
      }
      if (rules.max_pages) {
        setMaxPages(rules.max_pages.toString());
      }

      // Load content requirements
      setContentRequirements({
        ocrReadable: rules.ocr_required === 1,
        noPasswordProtected: rules.no_password_pdf === 1,
        barcodeQrCode: rules.barcode_required === 1,
        signatureStamp: rules.signature_required === 1 // Load from API response
      });

      // Load metadata requirements
      setMetadataRequired(rules.metadata_required === 1);

      // Load expiry validation
      setExpiryValidation(rules.expiry_validation === 1);

      setExistingDataLoaded(true);
    } catch (error) {
      // Error loading existing validation rules
    } finally {
      setLoading(false);
    }
  };

  // Function to validate file naming patterns
  // const validateNamingPatterns = (patterns) => {
  //   const validPlaceholders = [
  //     '<Today\'s Date>',
  //     '<Document Name>',
  //     '<Last Name>',
  //     '<First Name>',
  //     '<Department>',
  //     '<Document Type>',
  //     '<Timestamp>',
  //     '<Reference ID>'
  //   ];

  //   for (const pattern of patterns) {
  //     if (pattern.trim().length === 0) continue;

  //     // Check pattern length
  //     if (pattern.trim().length > 200) {
  //       return {
  //         isValid: false,
  //         message: `Pattern "${pattern}" is too long (max 200 characters)`
  //       };
  //     }

  //     // Check if pattern contains at least one valid placeholder
  //     const hasValidPlaceholder = validPlaceholders.some(placeholder =>
  //       pattern.includes(placeholder)
  //     );

  //     if (!hasValidPlaceholder) {
  //       return {
  //         isValid: false,
  //         message: `Pattern "${pattern}" should contain at least one valid placeholder`
  //       };
  //     }
  //   }

  //   return { isValid: true };
  // };

  const toggleRequirement = (requirement) => {
    setContentRequirements(prev => ({
      ...prev,
      [requirement]: !prev[requirement]
    }));
  };

  const toggleMetadata = () => {
    setMetadataRequired(!metadataRequired);
  };

  // const addNamingPattern = () => {
  //   if (namingPattern.trim()) {
  //     // Validate the pattern before adding
  //     const patternValidation = validateNamingPatterns([namingPattern.trim()]);
  //     if (!patternValidation.isValid) {
  //       toast.error(patternValidation.message);
  //       return;
  //     }

  //     if (currentPatterns.length >= 20) {
  //       toast.error('Cannot add more than 20 patterns');
  //       return;
  //     }

  //     setCurrentPatterns([...currentPatterns, namingPattern.trim()]);
  //     setNamingPattern('');
  //   }
  // };

  // const removeNamingPattern = (index) => {
  //   setCurrentPatterns(currentPatterns.filter((_, i) => i !== index));
  // };

  // const insertPlaceholder = (placeholder) => {
  //   const newPattern = namingPattern + placeholder;
  //   if (newPattern.length > 200) {
  //     toast.error('Pattern is too long (max 200 characters)');
  //     return;
  //   }
  //   setNamingPattern(newPattern);
  // };

  // Input validation functions
  const validateNumericInput = (value, fieldName, min, max) => {
    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < min || numValue > max) {
      toast.error(`${fieldName} must be between ${min} and ${max}`);
      return false;
    }
    return true;
  };

  const formatNumericInput = (value, min, max) => {
    const numValue = parseInt(value);
    if (isNaN(numValue)) return '';
    if (numValue < min) return min.toString();
    if (numValue > max) return max.toString();
    return numValue.toString();
  };

  const handleMinSizeChange = (e) => {
    const value = e.target.value;

    // Clear previous error
    setMinSizeError('');

    if (value === '') {
      setMinSize(value);
      return;
    }

    const numValue = parseInt(value);
    if (isNaN(numValue) || numValue < 1 || numValue > 102400) {
      setMinSizeError('Minimum size must be between 1 and 102400 KB');
      return;
    }

    // Remove the real-time validation for maximum size relationship
    // This will now only be checked when Save button is clicked

    setMinSize(value);
  };

  const handleMaxSizeChange = (e) => {
    const value = e.target.value;

    // Clear previous error
    setMaxSizeError('');

    if (value === '') {
      setMaxSize(value);
      return;
    }

    const numValue = parseInt(value);
    if (isNaN(numValue)) {
      setMaxSizeError('Please enter a valid number');
      return;
    }

    if (numValue > 102400) {
      setMaxSizeError('Maximum file size cannot exceed 100 MB (102400 KB)');
      return;
    }

    if (numValue < 1) {
      setMaxSizeError('Maximum size must be at least 1 KB');
      return;
    }

    // Remove the real-time validation for minimum size relationship
    // This will now only be checked when Save button is clicked

    setMaxSize(value);
  };

  const handleResolutionChange = (e) => {
    const value = e.target.value;

    // Clear previous error
    setResolutionError('');

    if (value === '') {
      setResolution(value);
      return;
    }

    const numValue = parseInt(value);

    setResolution(value);
  };

  const handleMaxPagesChange = (e) => {
    const value = e.target.value;

    // Clear previous error
    setMaxPagesError('');

    if (value === '') {
      setMaxPages(value);
      return;
    }


    setMaxPages(value);
  };

  const handleMinSizeBlur = (e) => {
    const value = e.target.value;
    if (value !== '') {
      const formattedValue = formatNumericInput(value, 1, 102400);
      setMinSize(formattedValue);
    }
  };

  const handleMaxSizeBlur = (e) => {
    const value = e.target.value;
    if (value !== '') {
      // Only check if value exceeds 100MB limit
      if (parseInt(value) > 102400) {
        setMaxSize('102400'); // Reset to max allowed value
        return;
      }

      // Update the state if valid
      setMaxSize(value);
    }
  };

  const handleResolutionBlur = (e) => {
    const value = e.target.value;
    if (value !== '') {
      const formattedValue = formatNumericInput(value, 72, 1200);
      setResolution(formattedValue);
    }
  };

  const handleMaxPagesBlur = (e) => {
    const value = e.target.value;
    if (value !== '') {
      const formattedValue = formatNumericInput(value, 1, 1000);
      setMaxPages(formattedValue);
    }
  };

  const toggleExpiryValidation = () => {
    setExpiryValidation(!expiryValidation);
  };

  const handleFormatChange = (selectedOptions) => {
    setSelectedFormats(selectedOptions || []);
  };


  const saveConfiguration = async () => {
    // Clear all previous errors
    setMinSizeError('');
    setMaxSizeError('');
    setResolutionError('');
    setMaxPagesError('');
    // setFormatsError(''); // Removed format validation error

    if (!envelopeId) {

      return;
    }

    const seleeveIdNum = formatEnvelopeId(envelopeId);

    if (!seleeveIdNum) {
      return;
    }

    // Validate form data - removed format validation requirement
    // if (!selectedFormats || selectedFormats.length === 0) {
    //   setFormatsError('Please select at least one file format');
    //   toast.error('Please select at least one file format');
    //   return;
    // }

    // if (selectedFormats.length > 10) {
    //   setFormatsError('Cannot select more than 10 file formats');
    //   return;
    // }

    // This validation now only happens when Save button is clicked
    if (parseInt(minSize) >= parseInt(maxSize)) {
      setMaxSizeError('Maximum size must be greater than minimum size');
      toast.error('Maximum size must be greater than minimum size');
      return;
    }

    if (parseInt(minSize) < 1) {
      setMinSizeError('Minimum file size must be at least 1 KB');
      return;
    }

    if (parseInt(minSize) < 0 || parseInt(maxSize) < 0) {
      setMinSizeError('File sizes must be positive numbers');
      return;
    }

    if (parseInt(maxSize) > 102400) { // 100 MB limit
      setMaxSizeError('Maximum file size cannot exceed 100 MB (102400 KB)');
      return;
    }

    // if (parseInt(resolution) < 72) {
    //   setResolutionError('Resolution must be at least 72 DPI');
    //   return;
    // }

    // if (parseInt(resolution) > 1200) { // Reasonable DPI limit
    //   setResolutionError('Resolution cannot exceed 1200 DPI');
    //   return;
    // }

    if (parseInt(maxPages) < 1) {
      setMaxPagesError('Maximum pages must be at least 1');
      return;
    }

    if (parseInt(maxPages) > 1000) { // Reasonable page limit
      setMaxPagesError('Maximum page count cannot exceed 1000');
      return;
    }

    // if (currentPatterns.length === 0) {
    //   toast.error('Please add at least one file naming pattern');
    //   return;
    // }

    // if (currentPatterns.length > 20) {
    //   toast.error('Cannot add more than 20 file naming patterns');
    //   return;
    // }

    // Validate file naming patterns
    // const patternValidation = validateNamingPatterns(currentPatterns);
    // if (!patternValidation.isValid) {
    //   toast.error(patternValidation.message);
    //   return;
    // }

    setLoading(true);

    try {
      // Clean and validate file naming patterns
      // const cleanedPatterns = currentPatterns
      //   .filter(pattern => pattern.trim().length > 0)
      //   .map(pattern => pattern.trim());

      // if (cleanedPatterns.length === 0) {
      //   toast.error('Please add at least one valid file naming pattern');
      //   setLoading(false);
      //   return;
      // }

      // Prepare payload according to the API specification
      const payload = {
        id: seleeveIdNum,
        allowed_formats: selectedFormats && selectedFormats.length > 0 ? selectedFormats.map(format => format.value) : [],
        min_size_kb: parseInt(minSize),
        max_size_kb: parseInt(maxSize),
        min_dpi: parseInt(resolution),
        max_pages: parseInt(maxPages),
        ocr_required: contentRequirements.ocrReadable,
        no_password_pdf: contentRequirements.noPasswordProtected,
        barcode_required: contentRequirements.barcodeQrCode,
        signature_required: contentRequirements.signatureStamp,
        metadata_required: metadataRequired,
        // file_naming_patterns: cleanedPatterns,
        expiry_validation: expiryValidation
      };

      const response = await  manageEnvelopeDocRules(payload);

      // Check if the response contains a success message
      if (response.data?.message && response.data.message.includes('successfully')) {
        toast.success(response.data.message);
        setIsSuccess(true);
        // Reset success state after 3 seconds
        setTimeout(() => setIsSuccess(false), 3000);
      } else {
        toast.error(response.data?.message || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Failed to save configuration. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <div className="page-loading">
        <img src="/images/loader.gif" alt="loader" />
      </div>}
      <div className={`document-validation-container ${isReadOnly ? 'readonly-container' : ''}`}>
        <h2 className="mb-2">Document Validation Configuration</h2>

        <div className="validation-sections">
          {/* Section 1: File Properties */}
          <div className="validation-settings-panel">
            <div className="panel-header-section">
              <div className="header-icon-wrapper">
                <i className="bi bi-file-earmark-text panel-icon"></i>
              </div>
              <h3 className="panel-title">File Properties</h3>
            </div>

            <div className="panel-content-wrapper">
              <div className="form-grid-layout">
                {/* File Format Selection */}
                <div className="form-field-group format-selection-group">
                  <div className="row">
                    <div className="col-12 col-md-3">
                      <label className="field-label-text">
                        Allowed File Formats
                      </label>
                    </div>
                    <div className="col-12 col-md-8">
                      <Select
                        isMulti
                        isDisabled={isReadOnly}
                        value={selectedFormats}
                        onChange={handleFormatChange}
                        options={formats}
                        placeholder="Select file formats..."
                        className="react-select-container"
                        classNamePrefix="react-select"
                        noOptionsMessage={() => "No file formats available"}
                        isClearable={!isReadOnly}
                        isSearchable={!isReadOnly}
                        menuPlacement="auto"
                        maxMenuHeight={300}
                        menuIsOpen={undefined}
                        closeMenuOnSelect={false}
                        hideSelectedOptions={false}
                        blurInputOnSelect={false}
                        loadingMessage={() => "Loading formats..."}
                        isOptionDisabled={() => false}
                        filterOption={(option, inputValue) => {
                          return option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
                            option.value.toLowerCase().includes(inputValue.toLowerCase());
                        }}
                        styles={{
                          control: (provided, state) => ({
                            ...provided,
                            minHeight: '48px',
                            border: state.isFocused ? '2px solid #007bff' : '2px solid #e9ecef',
                            borderRadius: '8px',
                            boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 123, 255, 0.1)' : 'none',
                            '&:hover': {
                              borderColor: '#007bff'
                            }
                          }),
                          multiValue: (provided) => ({
                            ...provided,
                            backgroundColor: '#e3f2fd',
                            border: '1px solid #bbdefb',
                            borderRadius: '20px',
                            padding: '4px 8px'
                          }),
                          multiValueLabel: (provided) => ({
                            ...provided,
                            color: '#1976d2',
                            fontWeight: '500'
                          }),
                          multiValueRemove: (provided) => ({
                            ...provided,
                            color: '#1976d2',
                            '&:hover': {
                              backgroundColor: '#1976d2',
                              color: 'white'
                            }
                          }),
                          option: (provided, state) => ({
                            ...provided,
                            backgroundColor: state.isSelected ? '#e3f2fd' : state.isFocused ? '#f8f9fa' : 'white',
                            color: state.isSelected ? '#1976d2' : '#495057',
                            cursor: 'pointer',
                            '&:active': {
                              backgroundColor: '#bbdefb'
                            }
                          }),
                          menu: (provided) => ({
                            ...provided,
                            border: '2px solid #e9ecef',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            marginTop: '4px',
                            zIndex: 9999
                          }),
                          menuList: (provided) => ({
                            ...provided,
                            maxHeight: '300px',
                            padding: '8px 0'
                          }),
                          input: (provided) => ({
                            ...provided,
                            margin: '0px'
                          }),
                          valueContainer: (provided) => ({
                            ...provided,
                            padding: '2px 8px'
                          })
                        }}
                      />

                    </div>
                  </div>
                  {/* <div className="format-selection-hint">
                    {selectedFormats.length > 0
                      ? `Selected formats: ${selectedFormats.join(', ')}`
                      : 'Click to select multiple file formats'
                    }
                  </div> */}
                </div>

                {/* File Size Settings */}
                <div className="form-field-group size-settings-group">
                  <div className="size-input-row">
                    <div className={`size-input-field ${minSizeError ? 'error' : ''}`}>
                      <label className="field-label-text">
                        Minimum Size (KB)
                      </label>
                      <input
                        type="number"
                        className={`size-input-control ${isReadOnly ? 'readonly' : ''}`}
                        value={minSize}
                        onChange={handleMinSizeChange}
                        onBlur={handleMinSizeBlur}
                        placeholder=""
                        readOnly={isReadOnly}
                      />
                      {minSizeError && (
                        <div className="error-display">
                          <span className="error-text">{minSizeError}</span>
                        </div>
                      )}
                    </div>

                    <div className={`size-input-field ${maxSizeError ? 'error' : ''}`}>
                      <label className="field-labe
                      l-text">
                        Maximum Size (KB)
                      </label>
                      <input
                        type="number"
                        className={`size-input-control ${isReadOnly ? 'readonly' : ''}`}
                        value={maxSize}
                        onChange={handleMaxSizeChange}
                        onBlur={handleMaxSizeBlur}
                        placeholder=""
                        max="102400"
                        readOnly={isReadOnly}
                      />
                      {maxSizeError && (
                        <div className="error-display">
                          <span className="error-text">{maxSizeError}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Quality Settings */}
                <div className="form-field-group quality-settings-group">
                  <div className="quality-input-row">
                    <div className={`quality-input-field ${resolutionError ? 'error' : ''}`}>
                      <label className="field-label-text">
                        Resolution / DPI
                      </label>
                      <input
                        type="number"
                        className={`quality-input-control ${isReadOnly ? 'readonly' : ''}`}
                        value={resolution}
                        onChange={handleResolutionChange}
                        onBlur={handleResolutionBlur}
                        placeholder=""
                        readOnly={isReadOnly}
                      />
                      <small className="quality-input-hint">Minimum recommended: 300 DPI for text documents</small>
                      {resolutionError && (
                        <div className="error-display">
                          <span className="error-text">{resolutionError}</span>
                        </div>
                      )}
                    </div>

                    <div className={`quality-input-field ${maxPagesError ? 'error' : ''}`}>
                      <label className="field-label-text">
                        Maximum Page Count
                      </label>
                      <input
                        type="number"
                        className={`quality-input-control ${isReadOnly ? 'readonly' : ''}`}
                        value={maxPages}
                        onChange={handleMaxPagesChange}
                        onBlur={handleMaxPagesBlur}
                        placeholder=""
                        readOnly={isReadOnly}
                      />
                      {maxPagesError && (
                        <div className="error-display">
                          <span className="error-text">{maxPagesError}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 2: Content Requirements */}
          <div className="validation-settings-panel">
            <div className="panel-header-section">
              <div className="header-icon-wrapper">
                <i className="bi bi-shield-check panel-icon"></i>
              </div>
              <h3 className="panel-title">Content Requirements</h3>
            </div>

            <div className="panel-content-wrapper">
              <div className="requirements-grid-layout">
                <div className="requirement-card">
                  <div className="requirement-content">
                    <div className="requirement-icon-wrapper">
                      <i className="bi bi-eye requirement-icon"></i>
                    </div>
                    <div className="requirement-text-content">
                      <h4 className="requirement-heading">OCR Readable</h4>
                      <p className="requirement-description">Require documents to contain readable text content</p>
                    </div>
                  </div>
                  <div
                    className={`requirement-toggle ${contentRequirements.ocrReadable ? 'toggle-active' : ''} ${loading ? 'disabled' : ''} ${isReadOnly ? 'readonly' : ''}`}
                    onClick={() => !loading && !isReadOnly && toggleRequirement('ocrReadable')}
                  >
                    <div className="toggle-handle"></div>
                  </div>
                </div>

                <div className="requirement-card">
                  <div className="requirement-content">
                    <div className="requirement-icon-wrapper">
                      <i className="bi bi-lock requirement-icon"></i>
                    </div>
                    <div className="requirement-text-content">
                      <h4 className="requirement-heading">No Password-Protected PDFs</h4>
                      <p className="requirement-description">Reject documents that are password protected</p>
                    </div>
                  </div>
                  <div
                    className={`requirement-toggle ${contentRequirements.noPasswordProtected ? 'toggle-active' : ''} ${loading ? 'disabled' : ''} ${isReadOnly ? 'readonly' : ''}`}
                    onClick={() => !loading && !isReadOnly && toggleRequirement('noPasswordProtected')}
                  >
                    <div className="toggle-handle"></div>
                  </div>
                </div>

                <div className="requirement-card">
                  <div className="requirement-content">
                    <div className="requirement-icon-wrapper">
                      <i className="bi bi-qr-code requirement-icon"></i>
                    </div>
                    <div className="requirement-text-content">
                      <h4 className="requirement-heading">Barcode/QR Code Presence</h4>
                      <p className="requirement-description">Require presence of barcodes or QR codes in documents</p>
                    </div>
                  </div>
                  <div
                    className={`requirement-toggle ${contentRequirements.barcodeQrCode ? 'toggle-active' : ''} ${loading ? 'disabled' : ''} ${isReadOnly ? 'readonly' : ''}`}
                    onClick={() => !loading && !isReadOnly && toggleRequirement('barcodeQrCode')}
                  >
                    <div className="toggle-handle"></div>
                  </div>
                </div>

                <div className="requirement-card">
                  <div className="requirement-content">
                    <div className="requirement-icon-wrapper">
                      <i className="bi bi-pen requirement-icon"></i>
                    </div>
                    <div className="requirement-text-content">
                      <h4 className="requirement-heading">Signature or Stamp Detection</h4>
                      <p className="requirement-description">Require presence of signatures or official stamps</p>
                    </div>
                  </div>
                  <div
                    className={`requirement-toggle ${contentRequirements.signatureStamp ? 'toggle-active' : ''} ${loading ? 'disabled' : ''} ${isReadOnly ? 'readonly' : ''}`}
                    onClick={() => !loading && !isReadOnly && toggleRequirement('signatureStamp')}
                  >
                    <div className="toggle-handle"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 3: Metadata Requirements */}
          <div className="validation-settings-panel">
            <div className="panel-header-section">
              <div className="header-icon-wrapper">
                <i className="bi bi-database panel-icon"></i>
              </div>
              <h3 className="panel-title">Metadata Requirements</h3>
            </div>

            <div className="panel-content-wrapper">
              <div className="metadata-requirements-layout">
                <div className="metadata-requirement-card">
                  <div className="metadata-requirement-content">
                    <div className="metadata-icon-wrapper">
                      <i className="bi bi-info-circle metadata-icon"></i>
                    </div>
                    <div className="metadata-text-content">
                      <h4 className="metadata-heading">Metadata Presence Required</h4>
                      <p className="metadata-description">Require documents to contain embedded metadata</p>
                    </div>
                  </div>
                  <div
                    className={`metadata-toggle ${metadataRequired ? 'toggle-active' : ''} ${loading ? 'disabled' : ''} ${isReadOnly ? 'readonly' : ''}`}
                    onClick={() => !loading && !isReadOnly && toggleMetadata()}
                  >
                    <div className="toggle-handle"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 4: File Naming Conventions */}
          {/* <div className="validation-settings-panel">
            <div className="panel-header-section">
              <div className="header-icon-wrapper">
                <i className="bi bi-file-text panel-icon"></i>
              </div>
              <h3 className="panel-title">
                File Naming Conventions
              </h3>
            </div>

            <div className="panel-content-wrapper">
              <div className="naming-conventions-layout">
                <div className="naming-instructions-text">
                  <p>Define patterns for acceptable file names. Use placeholders like &lt;Today's Date&gt;, &lt;Document Name&gt;, &lt;Last Name&gt;, &lt;First Name&gt;</p>
                </div>

                <div className="pattern-input-container">
                  <input
                    type="text"
                    className="pattern-input-field"
                    placeholder="Enter naming pattern..."
                    value={namingPattern}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value.length <= 200) {
                        setNamingPattern(value);
                      }
                    }}
                    maxLength={200}
                  />
                  <button
                    type="button"
                    className="add-pattern-button"
                    onClick={addNamingPattern}
                    disabled={!namingPattern.trim() || loading}
                  >
                    {loading ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <i className="bi bi-plus"></i>
                    )}
                  </button>
                </div>

                <div className="current-patterns-section">
                  <label className="patterns-label-text">Current Patterns:</label>
                  <div className="patterns-list-container">
                    {currentPatterns.map((pattern, index) => (
                      <div key={index} className="pattern-item-card">
                        <span className="pattern-text-content">{pattern}</span>
                        <button
                          type="button"
                          className="remove-pattern-button"
                          onClick={() => removeNamingPattern(index)}
                          disabled={currentPatterns.length === 0 || loading}
                        >
                          {loading ? (
                            <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          ) : (
                            '×'
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="placeholders-section">
                  <label className="placeholders-label-text">Common Placeholders:</label>
                  <div className="placeholders-grid-container">
                    {[
                      '<Today\'s Date>',
                      '<Document Name>',
                      '<Last Name>',
                      '<First Name>',
                      '<Department>',
                      '<Document Type>',
                      '<Timestamp>',
                      '<Reference ID>'
                    ].map((placeholder, index) => (
                      <button
                        key={index}
                        type="button"
                        className="placeholder-button"
                        onClick={() => insertPlaceholder(placeholder)}
                        disabled={namingPattern.length + placeholder.length > 200 || loading}
                      >
                        {placeholder}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div> */}

          {/* Section 5: Process/Compliance */}
          <div className="validation-settings-panel">
            <div className="panel-header-section">
              <div className="header-icon-wrapper">
                <i className="bi bi-check-circle panel-icon"></i>
              </div>
              <h3 className="panel-title">Process / Compliance</h3>
            </div>

            <div className="panel-content-wrapper">
              <div className="compliance-layout">
                <div className="compliance-item-card">
                  <div className="compliance-content">
                    <div className="compliance-icon-wrapper">
                      <i className="bi bi-clock compliance-icon"></i>
                    </div>
                    <div className="compliance-text-content">
                      <h4 className="compliance-heading">Expiry Validation</h4>
                      <p className="compliance-description">Check for document expiration dates and validate against current date</p>
                    </div>
                  </div>
                  <div
                    className={`compliance-toggle ${expiryValidation ? 'toggle-active' : ''} ${loading ? 'disabled' : ''} ${isReadOnly ? 'readonly' : ''}`}
                    onClick={() => !loading && !isReadOnly && toggleExpiryValidation()}
                  >
                    <div className="toggle-handle"></div>
                  </div>
                </div>

                <div className="compliance-notes-card">
                  <h4 className="compliance-notes-heading">Compliance Notes</h4>
                  <ul className="compliance-notes-list">
                    <li>Expiry validation will scan documents for date patterns and validate they haven't expired</li>
                    <li>Common date formats (MM/DD/YYYY, DD/MM/YYYY, YYYY-MM-DD) are automatically recognized</li>
                    <li>Documents without detectable expiry dates will pass validation unless explicitly rejected</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Global Action Buttons */}
        {!isReadOnly && (
          <div className="global-actions-container">
            <button
              type="button"
              className="btn-save-configuration"
              onClick={saveConfiguration}
              disabled={loading || !envelopeId}
            >
              Save Configuration
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export default DocumentValidation 